/**
 * 3ASH DEVICE: the write-a-review form (section td-review-form, docs/reviews/system.md, 22 Oct 2026).
 *
 * Photos are made smaller here (longest side 1600 px, JPEG) through a canvas, which also drops their EXIF
 * and GPS data. With a receiver (data-mode "worker") the review goes to the Cloudflare Worker: /start with
 * the Turnstile token, /photo for each photo, then /submit, and arrives in Shopify as a draft for approval.
 * Without one, or when sending fails, it goes by WhatsApp. A review sent to the receiver is kept in this
 * browser (localStorage td-rv-pending) so the device page shows it to its writer, marked as waiting for
 * approval (TdReviews in td-components.js).
 */

const SIM_ENDINGS = ['-nano-sim-esim', '-esim-only', '-dual-nano-sim', '-nano-sim'];
const MAX_SIDE = 1600;
const MAX_FILE = 25 * 1024 * 1024;
const PENDING_KEY = 'td-rv-pending';
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/** @type {Promise<any> | null} */
let turnstileScript = null;

/** Cloudflare Turnstile, loaded once and only when the form is used */
function loadTurnstile() {
  const win = /** @type {any} */ (window);
  if (win.turnstile) return Promise.resolve(win.turnstile);
  turnstileScript ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.onload = () => (win.turnstile ? resolve(win.turnstile) : reject(new Error('turnstile')));
    script.onerror = () => {
      turnstileScript = null;
      reject(new Error('turnstile'));
    };
    document.head.append(script);
  });
  return turnstileScript;
}

/** @param {string} handle - The model a SIM version belongs to: its handle without the version ending */
function modelHandle(handle) {
  const value = String(handle || '').trim().toLowerCase();
  for (const ending of SIM_ENDINGS) {
    if (value.endsWith(ending) && value.length > ending.length) return value.slice(0, -ending.length);
  }
  return value;
}

/**
 * @param {File} file
 * @returns {Promise<Blob>} a JPEG at most MAX_SIDE on its longest side, turned upright, without EXIF
 */
async function shrink(file) {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    await image.decode();
    const scale = Math.min(1, MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas');
    // A transparent PNG gets a white background instead of black
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
    if (!(blob instanceof Blob)) throw new Error('encode');
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

class SendError extends Error {
  /** @param {string} code */
  constructor(code) {
    super(code);
    this.code = code;
  }
}

/**
 * @typedef {{id: number, blob: Blob | null, url: string, item: HTMLElement}} Photo
 * @typedef {{device: string, rating: number, body: string, author: string, location: string, details: string,
 *   order: string, contact: string, lang: string, consent: {publish: boolean, policy: boolean, text: string}}} Payload
 */

class TdReviewForm extends HTMLElement {
  #abort = new AbortController();
  /** @type {Photo[]} */
  #photos = [];
  #nextId = 1;
  /** Photos are made smaller one after another; sending waits for the last one */
  #queue = Promise.resolve();
  /** @type {Record<string, string>} */
  #text = {};
  /** @type {Set<string>} fields shown as wrong, checked again as the shopper types */
  #invalid = new Set();
  #busy = false;
  #widget = '';
  #token = '';
  /** @type {Array<(token: string) => void>} */
  #waiting = [];

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    try {
      this.#text = JSON.parse(this.querySelector('[data-td-rf-text]')?.textContent || '{}');
    } catch {
      this.#text = {};
    }
    const form = this.querySelector('[data-td-rf-form]');
    if (!(form instanceof HTMLFormElement)) return;
    form.addEventListener('submit', this.#onSubmit, { signal });
    form.addEventListener('input', this.#onInput, { signal });
    form.addEventListener('change', this.#onInput, { signal });
    form.addEventListener('focusin', this.#prepareTurnstile, { signal, once: true });
    this.querySelector('[data-td-rf-file]')?.addEventListener('change', this.#onFiles, { signal });
    this.querySelector('[data-td-rf-thumbs]')?.addEventListener('click', this.#onThumbClick, { signal });
    this.querySelector('[data-td-rf-wa]')?.addEventListener('click', () => this.#sendWhatsApp(), { signal });
    this.#preselect();
    this.#count();
    this.#showWord();
    this.#refresh();
  }

  disconnectedCallback() {
    this.#abort.abort();
    for (const photo of this.#photos) if (photo.url) URL.revokeObjectURL(photo.url);
  }

  /** @param {string} name */
  #field(name) {
    return this.querySelector(`[data-td-rf-field="${name}"]`);
  }

  /** @param {string} name */
  #value(name) {
    if (name === 'rating') {
      const checked = this.querySelector('[data-td-rf-field="rating"]:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    }
    const field = this.#field(name);
    return field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement
      ? field.value.trim()
      : '';
  }

  /** @param {string} name */
  #valid(name) {
    const value = this.#value(name);
    switch (name) {
      case 'device':
        return value !== '';
      case 'rating':
        return Number(value) >= 1 && Number(value) <= 5;
      case 'body':
        return value.length >= 20 && value.length <= 3000;
      case 'author':
        return value.length >= 2 && value.length <= 40;
      case 'contact':
        if (!value) return true;
        if (value.includes('@')) return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        return value.replace(/\D/g, '').length >= 7;
      case 'consent':
        return Array.from(this.querySelectorAll('[data-td-rf-field="consent"]')).every(
          (box) => box instanceof HTMLInputElement && box.checked
        );
      default:
        return true;
    }
  }

  /** Shows or clears the field's message; @param {string} name */
  #check(name) {
    const ok = this.#valid(name);
    const error = this.querySelector(`[data-td-rf-error="${name}"]`);
    if (error instanceof HTMLElement) error.hidden = ok;
    const fields =
      name === 'rating' || name === 'consent'
        ? Array.from(this.querySelectorAll(`[data-td-rf-field="${name}"]`))
        : [this.#field(name)];
    for (const field of fields) {
      if (!(field instanceof HTMLElement)) continue;
      if (ok) field.removeAttribute('aria-invalid');
      else field.setAttribute('aria-invalid', 'true');
    }
    if (ok) this.#invalid.delete(name);
    else this.#invalid.add(name);
    return ok;
  }

  /** @returns {boolean} every field is right; otherwise the first wrong one has focus */
  #validateAll() {
    /** @type {Element | null | undefined} */
    let first = null;
    for (const name of ['device', 'rating', 'body', 'author', 'contact', 'consent']) {
      if (this.#check(name) || first) continue;
      if (name === 'rating') first = this.querySelector('[data-td-rf-field="rating"]');
      else if (name === 'consent')
        first = Array.from(this.querySelectorAll('[data-td-rf-field="consent"]')).find(
          (box) => box instanceof HTMLInputElement && !box.checked
        );
      else first = this.#field(name);
    }
    if (first instanceof HTMLElement) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      first.closest('.td-rf__step, .td-rf__consents')?.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
      first.focus({ preventScroll: true });
      return false;
    }
    return true;
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const name = target.dataset.tdRfField || '';
    if (name === 'device') this.#showDevice();
    if (name === 'rating') this.#showWord();
    if (name === 'body') this.#count();
    if (this.#invalid.has(name)) this.#check(name);
    this.#refresh();
  };

  /** Each step's number turns into a tick once the step is filled */
  #refresh() {
    const filled = [
      this.#valid('device'),
      this.#valid('rating'),
      this.#valid('body'),
      this.#photos.some((photo) => photo.blob),
      this.#valid('author'),
      Boolean(this.#value('order') || this.#value('contact')) && this.#valid('contact'),
    ];
    this.querySelectorAll('.td-rf__step').forEach((step, index) => step.classList.toggle('is-done', Boolean(filled[index])));
  }

  /** ?device=<handle> chooses the device (a SIM version chooses its model) */
  #preselect() {
    const select = this.#field('device');
    const wanted = modelHandle(new URLSearchParams(window.location.search).get('device') || '');
    if (select instanceof HTMLSelectElement && wanted && Array.from(select.options).some((option) => option.value === wanted)) {
      select.value = wanted;
    }
    this.#showDevice();
  }

  #showDevice() {
    const select = this.#field('device');
    const box = this.querySelector('[data-td-rf-device-img]');
    if (!(select instanceof HTMLSelectElement) || !(box instanceof HTMLElement)) return;
    const src = select.selectedOptions[0]?.dataset.img || '';
    let image = box.querySelector('img');
    if (!src) {
      image?.remove();
      box.classList.remove('has-image');
      return;
    }
    if (image?.getAttribute('src') === src) return;
    if (!image) {
      image = document.createElement('img');
      image.alt = '';
      image.width = 240;
      image.height = 240;
      image.decoding = 'async';
      box.append(image);
    }
    image.src = src;
    box.classList.add('has-image');
  }

  #showWord() {
    const checked = this.querySelector('[data-td-rf-field="rating"]:checked');
    const word = this.querySelector('[data-td-rf-star-word]');
    if (!word) return;
    const label = checked ? this.querySelector(`label[for="${CSS.escape(checked.id)}"]`) : null;
    word.textContent = label instanceof HTMLElement ? label.dataset.word || '' : '';
  }

  #count() {
    const out = this.querySelector('[data-td-rf-count]');
    if (!out) return;
    const length = this.#value('body').length;
    out.textContent = `${length} / 3000`;
    out.classList.toggle('is-ok', length >= 20);
  }

  /** @param {string} message */
  #photoMessage(message) {
    const error = this.querySelector('[data-td-rf-error="photos"]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /** @param {Event} event */
  #onFiles = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.files) return;
    const files = Array.from(input.files);
    input.value = '';
    this.#photoMessage('');
    this.#queue = this.#queue.then(() => this.#addPhotos(files));
  };

  /** @param {File[]} files */
  async #addPhotos(files) {
    const max = Number(this.dataset.maxPhotos) || 5;
    let message = '';
    for (const file of files) {
      if (this.#photos.length >= max) {
        message = this.#text.photosFull || '';
        break;
      }
      if (file.size > MAX_FILE) {
        message = this.#text.photoTooBig || '';
        continue;
      }
      const photo = this.#addTile();
      try {
        const blob = await shrink(file);
        if (!this.#photos.includes(photo)) continue;
        photo.blob = blob;
        photo.url = URL.createObjectURL(blob);
        this.#fillTile(photo);
      } catch {
        this.#removePhoto(photo.id);
        message = this.#text.photoError || '';
      }
    }
    this.#photoMessage(message);
    this.#syncAdd();
    this.#refresh();
  }

  /** @returns {Photo} a tile that shows a spinner while its photo is made smaller */
  #addTile() {
    const item = document.createElement('li');
    item.className = 'td-rf__thumb is-busy';
    this.querySelector('[data-td-rf-thumbs]')?.append(item);
    const photo = { id: this.#nextId++, blob: null, url: '', item };
    this.#photos.push(photo);
    this.#syncAdd();
    return photo;
  }

  /** @param {Photo} photo */
  #fillTile(photo) {
    const image = document.createElement('img');
    image.src = photo.url;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'td-rf__thumb-remove';
    remove.dataset.remove = String(photo.id);
    remove.innerHTML =
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 7l10 10M17 7L7 17"/></svg>';
    photo.item.replaceChildren(image, remove);
    photo.item.classList.remove('is-busy');
    this.#relabel();
  }

  /** Photo numbers in the alt texts and remove buttons follow the order on screen */
  #relabel() {
    this.#photos.forEach((photo, index) => {
      const number = String(index + 1);
      const image = photo.item.querySelector('img');
      if (image) image.alt = (this.#text.photoAlt || '').replace('[i]', number);
      photo.item.querySelector('[data-remove]')?.setAttribute('aria-label', (this.#text.photoRemove || '').replace('[i]', number));
    });
  }

  /** @param {Event} event */
  #onThumbClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-remove]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#removePhoto(Number(button.dataset.remove));
    this.#photoMessage('');
    this.#syncAdd();
    this.#refresh();
    const next = this.querySelector('[data-td-rf-thumbs] [data-remove]') || this.querySelector('[data-td-rf-file]');
    if (next instanceof HTMLElement) next.focus();
  };

  /** @param {number} id */
  #removePhoto(id) {
    const index = this.#photos.findIndex((photo) => photo.id === id);
    if (index < 0) return;
    const [photo] = this.#photos.splice(index, 1);
    if (photo?.url) URL.revokeObjectURL(photo.url);
    photo?.item.remove();
    this.#relabel();
  }

  /** The "Add photos" tile hides once the review has its most photos */
  #syncAdd() {
    const add = this.querySelector('[data-td-rf-add]');
    if (add instanceof HTMLElement) add.hidden = this.#photos.length >= (Number(this.dataset.maxPhotos) || 5);
  }

  #prepareTurnstile = () => {
    if (this.dataset.mode !== 'worker') return;
    loadTurnstile()
      .then((turnstile) => this.#renderTurnstile(turnstile))
      .catch(() => {});
  };

  /** Turnstile runs out of sight and asks the shopper something only when it must (interaction-only) */
  #renderTurnstile(/** @type {any} */ turnstile) {
    const box = this.querySelector('[data-td-rf-turnstile]');
    if (this.#widget || !(box instanceof HTMLElement)) return;
    this.#widget = turnstile.render(box, {
      sitekey: this.dataset.sitekey,
      action: 'review',
      appearance: 'interaction-only',
      size: 'flexible',
      callback: (/** @type {string} */ token) => {
        if (this.#waiting.length) this.#flush(token);
        else this.#token = token;
      },
      'expired-callback': () => {
        this.#token = '';
      },
      'error-callback': () => {
        this.#token = '';
        this.#flush('');
      },
    });
  }

  /** @param {string} token */
  #flush(token) {
    const waiting = this.#waiting;
    this.#waiting = [];
    for (const resolve of waiting) resolve(token);
  }

  /** @returns {Promise<string>} a token used once, or '' when Turnstile could not give one */
  async #getToken() {
    if (this.#token) {
      const token = this.#token;
      this.#token = '';
      return token;
    }
    try {
      this.#renderTurnstile(await loadTurnstile());
    } catch {
      return '';
    }
    return new Promise((resolve) => {
      let timer = 0;
      const done = (/** @type {string} */ token) => {
        window.clearTimeout(timer);
        resolve(token);
      };
      timer = window.setTimeout(() => {
        this.#waiting = this.#waiting.filter((waiter) => waiter !== done);
        resolve('');
      }, 30000);
      this.#waiting.push(done);
    });
  }

  /** @returns {{payload: Payload, title: string, url: string}} */
  #collect() {
    const select = this.#field('device');
    const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
    const consent = Array.from(this.querySelectorAll('[data-td-rf-consent]'))
      .map((node) => (node.textContent || '').replace(/\s+/g, ' ').trim())
      .join('\n');
    return {
      payload: {
        device: modelHandle(this.#value('device')),
        rating: Number(this.#value('rating')),
        body: this.#value('body'),
        author: this.#value('author'),
        location: this.#value('location'),
        details: [this.#value('condition'), this.#value('storage')].filter(Boolean).join(' · '),
        order: this.#value('order'),
        contact: this.#value('contact'),
        lang: document.documentElement.lang.toLowerCase().startsWith('ar') ? 'ar' : 'en',
        consent: { publish: true, policy: true, text: consent.slice(0, 1500) },
      },
      title: (option?.textContent || '').trim(),
      url: option?.dataset.url || '',
    };
  }

  /** @param {string} path */
  #url(path) {
    return `${(this.dataset.endpoint || '').replace(/\/+$/, '')}${path}`;
  }

  /** @param {Response} response */
  async #read(response) {
    /** @type {any} */
    let result = null;
    try {
      result = await response.json();
    } catch {
      result = null;
    }
    if (!response.ok || !result || result.ok !== true) throw new SendError(String(result?.error || `http_${response.status}`));
    return result;
  }

  /** @param {string} path @param {object} body */
  async #post(path, body) {
    const response = await fetch(this.#url(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return this.#read(response);
  }

  /** @param {string} session @param {Blob} blob */
  async #upload(session, blob) {
    const response = await fetch(this.#url('/photo'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${session}`, 'Content-Type': 'image/jpeg' },
      body: blob,
    });
    return this.#read(response);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    this.#hideAlert();
    if (!this.#validateAll()) return;
    if (this.dataset.mode !== 'worker') {
      this.#sendWhatsApp();
      return;
    }
    this.#setBusy(true);
    try {
      this.#progress(this.#text.sending || '');
      await this.#queue;
      const { payload, title, url } = this.#collect();
      const token = await this.#getToken();
      if (!token) throw new SendError('turnstile');
      const start = await this.#post('/start', { turnstile: token });
      /** @type {any} */ (window).turnstile?.reset(this.#widget);
      const ready = this.#photos.filter((photo) => photo.blob);
      /** @type {string[]} */
      const photos = [];
      for (const [index, photo] of ready.entries()) {
        this.#progress(
          (this.#text.sendingPhoto || '').replace('[i]', String(index + 1)).replace('[t]', String(ready.length))
        );
        const result = await this.#upload(start.session, /** @type {Blob} */ (photo.blob));
        photos.push(result.resourceUrl);
      }
      this.#progress(this.#text.sending || '');
      await this.#post('/submit', { ...payload, session: start.session, photos });
      this.#remember(payload);
      this.#done(payload.author, title, url, false);
    } catch (error) {
      const code = error instanceof SendError ? error.code : 'network';
      this.#fail(code.includes('turnstile') ? 'robot' : code === 'rate_limited' ? 'limit' : 'send');
    } finally {
      this.#setBusy(false);
    }
  };

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = this.querySelector('[data-td-rf-submit]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
    }
    if (!busy) this.#progress('');
  }

  /** @param {string} message */
  #progress(message) {
    const out = this.querySelector('[data-td-rf-progress]');
    if (out) out.textContent = message;
  }

  /** The review shows to its writer on the device page until it is published (TdReviews) */
  #remember(/** @type {Payload} */ payload) {
    try {
      const saved = JSON.parse(window.localStorage.getItem(PENDING_KEY) || '[]');
      const list = Array.isArray(saved) ? saved : [];
      list.push({ model: payload.device, author: payload.author, rating: payload.rating, body: payload.body, ts: Date.now() });
      window.localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-10)));
    } catch {
      // Private browsing or full storage: the review was sent all the same
    }
  }

  /** @param {'robot' | 'limit' | 'send'} kind limit: the receiver's daily or hourly limit for this connection */
  #fail(kind) {
    const alert = this.querySelector('[data-td-rf-alert]');
    const text = this.querySelector('[data-td-rf-alert-text]');
    const whatsapp = this.querySelector('[data-td-rf-wa]');
    if (!(alert instanceof HTMLElement) || !text) return;
    const t = this.#text;
    let message = (kind === 'robot' ? t.errRobot : kind === 'limit' ? t.errLimit || t.errSend : t.errSend) || '';
    if (whatsapp instanceof HTMLElement && this.dataset.wa) {
      whatsapp.hidden = false;
      message = `${message} ${this.#text.errWa || ''}`.trim();
    }
    text.textContent = message;
    alert.hidden = false;
    alert.scrollIntoView({ block: 'center' });
    alert.focus({ preventScroll: true });
  }

  #hideAlert() {
    const alert = this.querySelector('[data-td-rf-alert]');
    if (alert instanceof HTMLElement) alert.hidden = true;
  }

  /** Opens WhatsApp with the review written out; the photos are attached in the chat */
  #sendWhatsApp() {
    const number = this.dataset.wa || '';
    if (!number) {
      this.#fail('send');
      return;
    }
    if (!this.#validateAll()) return;
    const { payload, title, url } = this.#collect();
    const t = this.#text;
    const lines = [
      t.waMessage || '',
      '',
      `${t.waDevice}: ${title}${payload.details ? ` (${payload.details})` : ''}`,
      `${t.waRating}: ${'★'.repeat(payload.rating)}${'☆'.repeat(5 - payload.rating)} (${payload.rating}/5)`,
      `${t.waName}: ${payload.author}`,
    ];
    if (payload.location) lines.push(`${t.waCity}: ${payload.location}`);
    if (payload.order) lines.push(`${t.waOrder}: ${payload.order}`);
    lines.push('', `${t.waReview}:`, payload.body);
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(lines.join('\n'))}`, '_blank', 'noopener');
    this.#done(payload.author, title, url, true);
  }

  /**
   * @param {string} name @param {string} title @param {string} url
   * @param {boolean} whatsapp - sent by WhatsApp: the shopper still presses send there
   */
  #done(name, title, url, whatsapp) {
    const form = this.querySelector('[data-td-rf-form]');
    const done = this.querySelector('[data-td-rf-done]');
    if (!(form instanceof HTMLElement) || !(done instanceof HTMLElement)) return;
    const t = this.#text;
    const set = (/** @type {string} */ selector, /** @type {string} */ value) => {
      const node = done.querySelector(selector);
      if (node) node.textContent = value;
    };
    set('[data-td-rf-done-title]', whatsapp ? t.doneWaTitle || '' : (t.doneTitle || '').replace('[name]', name));
    set('[data-td-rf-done-text]', whatsapp ? t.doneWaText || '' : (t.doneText || '').replace('[device]', title));
    set('[data-td-rf-done-note]', whatsapp ? '' : t.doneNote || '');
    const back = done.querySelector('[data-td-rf-back]');
    if (back instanceof HTMLAnchorElement && url) {
      back.href = url;
      back.hidden = false;
    }
    form.hidden = true;
    done.hidden = false;
    done.scrollIntoView({ block: 'start' });
    done.focus({ preventScroll: true });
  }
}

if (!customElements.get('td-review-form')) customElements.define('td-review-form', TdReviewForm);
